import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import Button from "../../../components/Button"
import LoaderDots from "../../../components/LoaderDots"
import { useConfirm } from "../../../ConfirmProvider"
import { useNotification } from "../../../modules/Notification"
import { ERR_NOT_SUPPORTED, isGeneralOk, sendChargerCommand } from "./chargerCommand"
import { ButtonRow, CenteredLoader, Divider, FieldError, HelpText, MutedMessage, NumberInput, PanelTitle, Row, RowLabel, RowValue, Section, SectionTitle, Segment, SegmentedControl, StatusBadge } from "./integrationStyles"

// Modbus slave settings, following the apps' ModbusView (VLT-2103 / VLT-2104): msg 145 reads the config
// and live status, msg 147 writes the EVSE slave fields, and the Pi-side TCP gateway flag goes through
// msg 19 as general.mbtcp_enabled, in the same order the iOS app uses per mode (VLT-2695).

interface ModbusStatusReply {
  err_code?: number
  config?: { mbslave_enabled?: boolean, mbslave_address?: number, mbslave_timeout?: number, mbtcp_enabled?: boolean }
  status?: { link_lost?: boolean, query_ok?: number, query_fault?: number }
}

type ModbusMode = 'off' | 'rtu' | 'tcp'

const ADDRESS_MIN = 1
const ADDRESS_MAX = 247
const DEFAULT_ADDRESS = 11
// FW 3.57 raised the watchdog ceiling from 100 to 254 s (VLT-2707); 255 is the "off" sentinel, shown as 0.
const TIMEOUT_MAX = 254
const TIMEOUT_OFF_SENTINEL = 255
const DEFAULT_TIMEOUT = 20
const MODBUS_TCP_PORT = 502

const MODES: { value: ModbusMode, labelKey: string }[] = [
  { value: 'off', labelKey: 'charger_off' },
  { value: 'rtu', labelKey: 'charger_rtu' },
  { value: 'tcp', labelKey: 'charger_tcp' },
]

function modeFrom(config: ModbusStatusReply['config']): ModbusMode {
  if (config?.mbtcp_enabled) return 'tcp'
  if (config?.mbslave_enabled) return 'rtu'
  return 'off'
}

const isValidAddress = (value: number) => Number.isInteger(value) && value >= ADDRESS_MIN && value <= ADDRESS_MAX
const isValidTimeout = (value: number) => Number.isInteger(value) && ((value >= 0 && value <= TIMEOUT_MAX) || value === TIMEOUT_OFF_SENTINEL)

export default function ModbusPanel({ chargerId, ipAddress }: { chargerId: string, ipAddress: string }) {
  const { t } = useTranslation()
  const { addNotification } = useNotification()
  const confirm = useConfirm()

  const [reply, setReply] = useState<ModbusStatusReply | null>()
  const [mode, setMode] = useState<ModbusMode>('off')
  const [address, setAddress] = useState<string>(String(DEFAULT_ADDRESS))
  const [timeout, setTimeoutValue] = useState<string>(String(DEFAULT_TIMEOUT))
  const [isSaving, setIsSaving] = useState<boolean>(false)

  async function fetchStatus() {
    try {
      const status: ModbusStatusReply = await sendChargerCommand(chargerId, { msg_type: 145 })
      setReply(status)
      setMode(modeFrom(status.config))
      const reportedAddress = status.config?.mbslave_address
      setAddress(String(reportedAddress !== undefined && isValidAddress(reportedAddress) ? reportedAddress : DEFAULT_ADDRESS))
      const reportedTimeout = status.config?.mbslave_timeout
      setTimeoutValue(String(reportedTimeout === undefined ? DEFAULT_TIMEOUT : reportedTimeout === TIMEOUT_OFF_SENTINEL ? 0 : Math.max(0, reportedTimeout)))
    } catch (err) {
      console.log('Unable to read the Modbus status', err)
      setReply(null)
    }
  }

  useEffect(() => {
    fetchStatus()
  }, [chargerId])

  const savedMode = modeFrom(reply?.config)
  const addressNumber = Number(address)
  const timeoutNumber = Number(timeout)
  const addressError = mode !== 'off' && !isValidAddress(addressNumber)
  const timeoutError = mode !== 'off' && !isValidTimeout(timeoutNumber)
  const canSave = !addressError && !timeoutError

  async function save() {
    if (!canSave || isSaving) return
    // RTU takes the RS485 "A" connector, so the external meter and everything built on it stops working.
    if (mode === 'rtu' && savedMode !== 'rtu' && !(await confirm({ message: t('charger_inRtuModeTheChargerUsesTheRs485AConnectorSoTheExternalMeterVoltieMeterSensorboxMidCantBeUsedAndDlmMidBasedFeaturesStopWorkingContinue'), showCancel: true }))) return

    setIsSaving(true)
    try {
      const slave = (enabled: boolean) => sendChargerCommand(chargerId, mode === 'off'
        ? { msg_type: 147, mbslave_enabled: enabled }
        : { msg_type: 147, mbslave_enabled: enabled, mbslave_address: addressNumber, mbslave_timeout: timeoutNumber })
      const tcpGateway = (enabled: boolean) => sendChargerCommand(chargerId, { msg_type: 19, general: { mbtcp_enabled: enabled } })

      // TCP needs the slave enabled too: the FW refuses register writes while Modbus control is off.
      const replies = mode === 'off' ? [await slave(false), await tcpGateway(false)]
        : mode === 'rtu' ? [await tcpGateway(false), await new Promise(resolve => setTimeout(resolve, 300)).then(() => slave(true))]
        : [await slave(true), await tcpGateway(true)]

      if (replies.some(r => r?.err_code === ERR_NOT_SUPPORTED)) throw new Error('not supported')
      if (!replies.every(isGeneralOk)) throw new Error('modbus config refused')
      addNotification(t('charger_success'), 'TEXT')
    } catch (err) {
      console.log('Unable to save the Modbus settings', err)
      addNotification(t('charger_couldNotSaveTheSettingsOnTheCharger'), 'TEXT', 'error')
    }
    // Read back what the charger actually kept; older FW resets some values on restart (VLT-2555).
    await new Promise(resolve => setTimeout(resolve, 1000))
    await fetchStatus()
    setIsSaving(false)
  }

  return (
    <>
      <PanelTitle>{t('charger_modbus')}</PanelTitle>
      {reply === undefined && <CenteredLoader><LoaderDots /></CenteredLoader>}
      {reply === null && <Section><MutedMessage>{t('charger_couldNotLoadTheSettingsFromTheCharger')}</MutedMessage></Section>}
      {reply?.err_code === ERR_NOT_SUPPORTED && <Section><MutedMessage>{t('charger_theChargersSoftwareDoesNotSupportModbus')}</MutedMessage></Section>}
      {reply && reply.err_code !== ERR_NOT_SUPPORTED && <>
        <Section>
          <Row>
            <RowLabel>{t('charger_mode')}</RowLabel>
            <SegmentedControl role="radiogroup" aria-label={t('charger_mode')}>
              {MODES.map(option => (
                <Segment key={option.value} role="radio" aria-checked={mode === option.value} $selected={mode === option.value} onClick={() => setMode(option.value)}>
                  {t(option.labelKey)}
                </Segment>
              ))}
            </SegmentedControl>
          </Row>

          {mode !== 'off' && <>
            <Divider />
            <Row>
              <RowLabel>{t('charger_address')}</RowLabel>
              <NumberInput type="number" aria-label={t('charger_address')} $isError={addressError} value={address} onChange={e => setAddress(e.target.value)} />
            </Row>
            {addressError && <FieldError>{t('charger_addressMustBeBetween1And247')}</FieldError>}
            <Row style={{ marginTop: '8px' }}>
              <RowLabel>{t('charger_timeoutS')}</RowLabel>
              <NumberInput type="number" aria-label={t('charger_timeoutS')} $isError={timeoutError} value={timeout} onChange={e => setTimeoutValue(e.target.value)} />
            </Row>
            {timeoutError && <FieldError>{t('charger_timeoutMustBeBetween0And254Seconds')}</FieldError>}
            <HelpText>{t('charger_ifTheModbusMasterStaysSilentThisLongTheChargerSetsTheChargingCurrentTo0AAndChargingStops')}</HelpText>

            {/* The TCP server listens on the charger's own address; nothing here is settable. */}
            {mode === 'tcp' && <>
              <Divider />
              <Row>
                <RowLabel>{t('charger_ipAddress')}</RowLabel>
                <RowValue>{ipAddress}</RowValue>
              </Row>
              <Row>
                <RowLabel>{t('charger_port')}</RowLabel>
                <RowValue>{MODBUS_TCP_PORT}</RowValue>
              </Row>
            </>}
          </>}
        </Section>

        {savedMode !== 'off' && reply.status && (
          <Section>
            <SectionTitle>{t('charger_status')}</SectionTitle>
            <Row>
              <RowLabel>{t('charger_link')}</RowLabel>
              <StatusBadge $tone={reply.status.link_lost ? 'danger' : 'success'}>{t(reply.status.link_lost ? 'charger_disconnected' : 'charger_connected')}</StatusBadge>
            </Row>
            <Row>
              <RowLabel>{t('charger_queriesOk')}</RowLabel>
              <RowValue>{reply.status.query_ok ?? '-'}</RowValue>
            </Row>
            <Row>
              <RowLabel>{t('charger_queryFaults')}</RowLabel>
              <RowValue>{reply.status.query_fault ?? '-'}</RowValue>
            </Row>
          </Section>
        )}

        <ButtonRow style={{ opacity: canSave ? 1 : 0.5, pointerEvents: canSave ? 'auto' : 'none' }}>
          <Button label={isSaving ? <LoaderDots /> : t('charger_save')} width="120px" onclick={save} />
        </ButtonRow>
      </>}
    </>
  )
}
